import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
    ArrowLeft,
    ExternalLink,
    Loader2,
    ShieldAlert,
    AlertTriangle,
    AlertCircle,
    Info,
    Check,
    EyeOff,
    BookmarkCheck,
} from "lucide-react";

import { Breadcrumb } from "../components/Breadcrumb";
import { usePageMeta } from "../lib/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEVERITY_STYLES = {
    Critical: { color: "text-accent-red", bg: "bg-accent-red/15", Icon: AlertCircle },
    High: { color: "text-accent-amber", bg: "bg-accent-amber/15", Icon: AlertTriangle },
    Medium: { color: "text-yellow-400", bg: "bg-yellow-500/15", Icon: AlertTriangle },
    Low: { color: "text-text-muted", bg: "bg-zinc-700/40", Icon: Info },
    None: { color: "text-text-dim", bg: "bg-zinc-700/40", Icon: Info },
};

const STATUS_OPTIONS = [
    { value: "acknowledged", label: "Acknowledge", Icon: BookmarkCheck },
    { value: "resolved", label: "Resolve", Icon: Check },
    { value: "suppressed", label: "Suppress", Icon: EyeOff },
];

const isoToHuman = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toUTCString();
};

export default function AlertDetail() {
    const { alertId } = useParams();
    const navigate = useNavigate();
    const [alert, setAlert] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");
    const [notes, setNotes] = useState("");

    usePageMeta({
        title: alert?.cve_id
            ? `${alert.cve_id} · CVE Alert · Cybersecurity Forefront`
            : "CVE Alert · Cybersecurity Forefront",
        description: alert?.cve_summary?.slice(0, 160) || "CVE alert detail.",
    });

    const load = async () => {
        setLoading(true);
        setErrorMsg("");
        try {
            const { data } = await axios.get(`${API}/cve-alerts/${alertId}`, {
                withCredentials: true,
            });
            setAlert(data);
            setNotes(data.notes || "");
        } catch (err) {
            if (err?.response?.status === 404) {
                setErrorMsg("Alert not found.");
            } else if (err?.response?.status === 402) {
                setErrorMsg("Upgrade to Pro to access CVE alerts.");
            } else if (err?.response?.status === 401) {
                navigate("/login");
                return;
            } else {
                setErrorMsg(err?.response?.data?.detail || "Failed to load alert.");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [alertId]);

    const patch = async (body) => {
        setSaving(true);
        try {
            const { data } = await axios.patch(
                `${API}/cve-alerts/${alertId}`,
                body,
                { withCredentials: true },
            );
            setAlert(data);
            if (typeof body.notes === "string") setNotes(data.notes || "");
        } catch (err) {
            setErrorMsg(err?.response?.data?.detail || "Update failed.");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="pt-24 md:pt-28 pb-20 mx-auto max-w-3xl px-5 text-center text-text-muted">
                <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2 text-accent-cyan" />
                Loading alert…
            </div>
        );
    }

    if (errorMsg || !alert) {
        return (
            <div className="pt-24 md:pt-28 pb-20 mx-auto max-w-3xl px-5 text-center">
                <p className="text-text-muted">{errorMsg || "Alert unavailable."}</p>
                <Link
                    to="/alerts"
                    className="inline-flex items-center gap-1.5 mt-4 text-accent-cyan text-sm hover:underline"
                >
                    <ArrowLeft className="w-3.5 h-3.5" /> Back to alerts
                </Link>
            </div>
        );
    }

    const sev = SEVERITY_STYLES[alert.cvss_severity || "None"] || SEVERITY_STYLES.None;
    const SevIcon = sev.Icon;

    return (
        <div
            className="pt-24 md:pt-28 pb-20 mx-auto max-w-4xl px-5 md:px-10"
            data-testid="alert-detail-page"
        >
            <Breadcrumb
                items={[
                    { name: "Home", to: "/" },
                    { name: "CVE Alerts", to: "/alerts" },
                    { name: alert.cve_id, to: `/alerts/${alert.id}` },
                ]}
            />

            <Link
                to="/alerts"
                className="inline-flex items-center gap-1.5 text-[12.5px] text-text-muted hover:text-accent-cyan mb-4"
            >
                <ArrowLeft className="w-3.5 h-3.5" /> All alerts
            </Link>

            {/* Header */}
            <div className="rounded-2xl border border-white/[0.08] bg-surface/60 backdrop-blur-md p-6 mb-5">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                        <p className="text-[10.5px] font-mono uppercase tracking-[0.32em] text-accent-cyan mb-1">
                            CVE Alert
                        </p>
                        <h1 className="text-2xl sm:text-3xl font-semibold text-text-primary tracking-tight font-mono">
                            {alert.cve_id}
                        </h1>
                    </div>
                    <div
                        className={`inline-flex items-center gap-2 rounded-md ${sev.bg} ${sev.color} px-3 py-1.5`}
                    >
                        <SevIcon className="w-4 h-4" />
                        <span className="font-mono text-sm uppercase">
                            {alert.cvss_severity}
                        </span>
                        <span className="font-mono text-sm">
                            · {alert.cvss_score?.toFixed(1) ?? "—"}
                        </span>
                    </div>
                </div>
                <p className="text-text-muted text-[14px] leading-relaxed mt-4 max-w-3xl">
                    {alert.cve_summary}
                </p>
                {alert.cvss_vector && (
                    <p className="font-mono text-[11.5px] text-text-dim mt-3">
                        {alert.cvss_vector}
                    </p>
                )}
            </div>

            {/* Affected hardware */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                <div className="rounded-xl border border-white/[0.06] bg-surface/40 p-5">
                    <h2 className="text-[11px] font-mono uppercase tracking-[0.32em] text-text-dim mb-3">
                        Affected hardware
                    </h2>
                    {alert.hardware ? (
                        <dl className="grid grid-cols-2 gap-y-1.5 text-[13px]">
                            <dt className="text-text-dim">Vendor</dt>
                            <dd className="text-text-primary">
                                {alert.hardware.vendor || "—"}
                            </dd>
                            <dt className="text-text-dim">Model</dt>
                            <dd className="text-text-primary">
                                {alert.hardware.model || "—"}
                            </dd>
                            <dt className="text-text-dim">Hostname</dt>
                            <dd className="text-text-primary font-mono">
                                {alert.hardware.hostname || "—"}
                            </dd>
                            <dt className="text-text-dim">Serial</dt>
                            <dd className="text-text-primary font-mono">
                                {alert.hardware.serial_number || "—"}
                            </dd>
                            <dt className="text-text-dim">Firmware</dt>
                            <dd className="text-text-primary font-mono">
                                {alert.hardware.firmware_version || "—"}
                            </dd>
                            <dt className="text-text-dim">Matched CPE</dt>
                            <dd className="text-accent-cyan font-mono text-[11.5px] break-all">
                                {alert.matched_cpe || "—"}
                            </dd>
                        </dl>
                    ) : (
                        <p className="text-text-dim text-[13px]">
                            Hardware no longer in inventory.
                        </p>
                    )}
                    {alert.client?.slug && (
                        <Link
                            to={`/clients/${alert.client.slug}`}
                            className="inline-flex items-center gap-1 text-accent-cyan text-[12.5px] mt-3 hover:underline"
                        >
                            Open client: {alert.client.name}{" "}
                            <ExternalLink className="w-3 h-3" />
                        </Link>
                    )}
                </div>

                <div className="rounded-xl border border-white/[0.06] bg-surface/40 p-5">
                    <h2 className="text-[11px] font-mono uppercase tracking-[0.32em] text-text-dim mb-3">
                        Timeline
                    </h2>
                    <dl className="grid grid-cols-1 gap-y-2 text-[13px]">
                        <div className="flex justify-between">
                            <dt className="text-text-dim">Published (NVD)</dt>
                            <dd className="text-text-primary font-mono text-[12px]">
                                {isoToHuman(alert.cve_published)}
                            </dd>
                        </div>
                        <div className="flex justify-between">
                            <dt className="text-text-dim">NVD last modified</dt>
                            <dd className="text-text-primary font-mono text-[12px]">
                                {isoToHuman(alert.cve_last_modified)}
                            </dd>
                        </div>
                        <div className="flex justify-between">
                            <dt className="text-text-dim">Discovered by scan</dt>
                            <dd className="text-text-primary font-mono text-[12px]">
                                {isoToHuman(alert.discovered_at)}
                            </dd>
                        </div>
                        <div className="flex justify-between">
                            <dt className="text-text-dim">Last seen</dt>
                            <dd className="text-text-primary font-mono text-[12px]">
                                {isoToHuman(alert.last_seen_at)}
                            </dd>
                        </div>
                        <div className="flex justify-between">
                            <dt className="text-text-dim">Status</dt>
                            <dd className="text-text-primary font-mono text-[12px] uppercase">
                                {alert.status}
                            </dd>
                        </div>
                    </dl>
                </div>
            </div>

            {/* References */}
            {alert.references?.length > 0 && (
                <div className="rounded-xl border border-white/[0.06] bg-surface/40 p-5 mb-5">
                    <h2 className="text-[11px] font-mono uppercase tracking-[0.32em] text-text-dim mb-3">
                        References
                    </h2>
                    <ul className="space-y-1.5 text-[13px]">
                        {alert.references.map((url, idx) => (
                            <li key={idx}>
                                <a
                                    href={url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-accent-cyan hover:underline inline-flex items-center gap-1"
                                >
                                    {url}{" "}
                                    <ExternalLink className="w-3 h-3 opacity-70" />
                                </a>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Actions */}
            <div className="rounded-xl border border-white/[0.06] bg-surface/40 p-5 mb-5">
                <h2 className="text-[11px] font-mono uppercase tracking-[0.32em] text-text-dim mb-3">
                    Actions
                </h2>
                <div className="flex flex-wrap gap-2">
                    {STATUS_OPTIONS.map(({ value, label, Icon }) => (
                        <button
                            key={value}
                            type="button"
                            disabled={saving || alert.status === value}
                            onClick={() => patch({ status: value })}
                            className="inline-flex items-center gap-1.5 rounded-md border border-white/[0.06] bg-canvas/40 px-3 py-1.5 text-[12.5px] text-text-muted hover:text-text-primary hover:border-white/15 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                            data-testid={`alert-action-${value}`}
                        >
                            <Icon className="w-3.5 h-3.5" />
                            {label}
                            {alert.status === value && (
                                <span className="text-accent-mint ml-1">·</span>
                            )}
                        </button>
                    ))}
                </div>
            </div>

            {/* Notes */}
            <div className="rounded-xl border border-white/[0.06] bg-surface/40 p-5">
                <h2 className="text-[11px] font-mono uppercase tracking-[0.32em] text-text-dim mb-3">
                    Notes (markdown supported)
                </h2>
                <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={5}
                    maxLength={4000}
                    placeholder="Any internal notes about this CVE — remediation steps, owner, downstream impact…"
                    className="w-full bg-canvas/50 border border-white/[0.06] rounded-md px-3 py-2 text-[13px] text-text-primary placeholder:text-text-dim focus:border-accent-cyan/50 focus:outline-none font-mono"
                    data-testid="alert-notes"
                />
                <div className="flex items-center justify-end mt-2">
                    <button
                        type="button"
                        onClick={() => patch({ notes })}
                        disabled={saving || notes === (alert.notes || "")}
                        className="inline-flex items-center gap-1.5 rounded-md bg-accent-cyan text-canvas px-3 py-1.5 text-[12.5px] font-semibold hover:bg-accent-cyan/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        data-testid="alert-save-notes"
                    >
                        {saving ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : null}
                        Save notes
                    </button>
                </div>
            </div>
        </div>
    );
}
